import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Dialog, Typography } from "@mui/material";
import KeyboardReturnRounded from "@mui/icons-material/KeyboardReturnRounded";
import { useNavigate } from "react-router-dom";
import { NavIcon } from "./navIcons";
import { NavGroup, NavPage, searchPages } from "./navModel";
import {
  IconTile,
  Kbd,
  NoMatch,
  PageLabel,
  SearchField,
  SectionLabel,
  isPlainClick,
} from "./navPrimitives";
import { NAV, overlayPaper, rowSx } from "./navTheme";
import {
  MOD_ENTER,
  MOD_SHIFT_ENTER,
  hasModifier,
} from "../../../utils/platform";

interface CommandPaletteProps {
  open: boolean;
  groups: NavGroup[];
  shortcuts: NavPage[];
  onClose: () => void;
}

const Hint: React.FC<{ keys: string; label: string }> = ({ keys, label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
    <Kbd>{keys}</Kbd>
    <Typography sx={{ fontSize: "0.6875rem", color: NAV.textMuted }}>
      {label}
    </Typography>
  </Box>
);

/** Only lists pages already in the permission-filtered nav. */
const CommandPalette: React.FC<CommandPaletteProps> = ({
  open,
  groups,
  shortcuts,
  onClose,
}) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const listRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const results = useMemo<NavPage[]>(
    () => (query.trim() ? searchPages(query, groups) : shortcuts),
    [query, groups, shortcuts],
  );

  useEffect(() => {
    if (open) {
      setQuery("");
      setHighlight(0);
    }
  }, [open]);

  useEffect(() => setHighlight(0), [query]);

  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${highlight}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [highlight]);

  const go = useCallback(
    (page?: NavPage) => {
      if (!page) return;
      navigate(page.link);
      onClose();
    },
    [navigate, onClose],
  );

  // Chrome picks foreground vs background from the held modifiers; not ours to set.
  const goNewTab = useCallback(
    (page?: NavPage) => {
      if (!page) return;
      if (!window.open(page.link, "_blank")) navigate(page.link);
      onClose();
    },
    [navigate, onClose],
  );

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlight((i) => (results.length ? (i + 1) % results.length : 0));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlight((i) =>
        results.length ? (i - 1 + results.length) % results.length : 0,
      );
    } else if (event.key === "Enter") {
      event.preventDefault();
      const page = results[highlight];
      if (hasModifier(event)) goNewTab(page);
      else go(page);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(10, 10, 10, 0.6)",
            backdropFilter: "blur(3px)",
          },
        },
        // autoFocus alone loses the race with MUI's focus trap under StrictMode.
        transition: { onEntered: () => inputRef.current?.focus() },
      }}
      PaperProps={{
        sx: {
          ...overlayPaper(18),
          alignSelf: "flex-start",
          mt: { xs: "12vh", sm: "14vh" },
          overflow: "hidden",
        },
      }}
    >
      <Box sx={{ px: 0.5, borderBottom: `1px solid ${NAV.border}` }}>
        <SearchField
          variant="flat"
          value={query}
          onChange={setQuery}
          onKeyDown={handleKeyDown}
          placeholder="Jump to a page…"
          ariaLabel="Jump to a page"
          height={56}
          fontSize="0.9375rem"
          iconSize={20}
          autoFocus
          inputRef={inputRef}
          trailing={
            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              <Kbd>ESC</Kbd>
            </Box>
          }
        />
      </Box>

      <Box
        ref={listRef}
        sx={{
          maxHeight: "52vh",
          overflowY: "auto",
          py: 1,
          px: 1,
        }}
      >
        {!query.trim() && shortcuts.length > 0 && (
          <SectionLabel sx={{ px: 1.5, py: 0.75 }}>
            Shortcuts
          </SectionLabel>
        )}

        {results.length === 0 && <NoMatch query={query} />}

        {results.map((page, index) => {
          const isHighlighted = index === highlight;
          return (
            <Box
              key={`${page.groupId}-${page.id}`}
              component="a"
              href={page.link}
              data-index={index}
              onMouseMove={() => setHighlight(index)}
              onClick={(event: React.MouseEvent) => {
                if (!isPlainClick(event)) return;
                event.preventDefault();
                go(page);
              }}
              sx={{
                ...rowSx({ height: 46, radius: 11, gap: 1.5, px: 1.5 }),
                color: isHighlighted ? NAV.textActive : NAV.textIdle,
                backgroundColor: isHighlighted ? NAV.hover : "transparent",
              }}
            >
              <IconTile active={isHighlighted}>
                <NavIcon
                  id={page.id}
                  groupId={page.groupId}
                  size={17}
                  color="inherit"
                />
              </IconTile>
              <PageLabel
                title={page.label}
                caption={page.groupLabel}
                titleSize="0.875rem"
              />
              {isHighlighted && (
                <KeyboardReturnRounded
                  sx={{ fontSize: 16, color: NAV.textMuted }}
                />
              )}
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          display: { xs: "none", sm: "flex" },
          alignItems: "center",
          gap: 2,
          px: 2,
          py: 1.25,
          borderTop: `1px solid ${NAV.border}`,
          backgroundColor: NAV.surfaceSunken,
        }}
      >
        <Hint keys="↑↓" label="Navigate" />
        <Hint keys="↵" label="Open" />
        <Hint keys={MOD_ENTER} label="New tab" />
        <Hint keys={MOD_SHIFT_ENTER} label="New tab + switch" />
      </Box>
    </Dialog>
  );
};

export default CommandPalette;
